import routes from '../../config/routes';
import zhCNMenu from '@/locales/zh-CN/menu';
import { menuLabelMatches } from '@/utils/menuSearch';
import { MENU_TITLE_MAP } from '@/utils/permissionCatalog';

export const COMMAND_PALETTE_OPEN_EVENT = 'comic10k:command-palette:open';

export const commandPaletteShortcutText = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform)
    ? '⌘K'
    : 'Ctrl K';

export const openCommandPalette = () => {
  window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT));
};

type RoutePermission = {
  code?: string;
  name?: string;
};

type RouteLike = {
  path?: string;
  name?: string;
  locale?: string;
  hideInMenu?: boolean;
  access?: string;
  redirect?: string;
  component?: string;
  permission?: RoutePermission | RoutePermission[];
  routes?: RouteLike[];
};

export type CommandPaletteAccess = Record<string, unknown> & {
  hasPermissionCodes?: (...codes: string[]) => boolean;
};

export type CommandPaletteItem = {
  key: string;
  title: string;
  breadcrumb: string[];
  href: string;
  type: 'menu' | 'tab';
};

type BuildCommandPaletteOptions = {
  currentPathname?: string;
  currentSearch?: string;
};

const APP_DECOR_TAB_BY_PATH: Record<string, string> = {
  '/growth-config/app-decor/base': 'base',
  '/growth-config/app-decor/banners': 'banners',
  '/growth-config/app-decor/recommended': 'recommended',
  '/growth-config/app-decor/categories': 'categories',
  '/growth-config/app-decor/comic-lists': 'comicLists',
  '/growth-config/app-decor/honeypot-pool': 'honeypotPool',
};

const AD_SLOT_TAB_TARGET_BY_PATH: Record<string, string> = {
  '/growth-config/ad-slots/tab': '/growth-config/ad-slots',
  '/growth-config/ad-slots/global-tab': '/growth-config/ad-slots/global-tab',
};

const normalizePermissions = (
  value?: RoutePermission | RoutePermission[],
): RoutePermission[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const routeLabel = (route: RouteLike, namePath: string[]) => {
  const localeKey =
    typeof route.locale === 'string'
      ? route.locale
      : route.name
        ? `menu.${[...namePath, route.name].join('.')}`
        : undefined;
  if (localeKey && localeKey in zhCNMenu) {
    return zhCNMenu[localeKey as keyof typeof zhCNMenu];
  }
  if (route.name && MENU_TITLE_MAP[route.name]) {
    return MENU_TITLE_MAP[route.name];
  }
  const permissionName = normalizePermissions(route.permission)
    .map((item) => item.name?.trim())
    .find(Boolean);
  if (permissionName) {
    return permissionName.replace(/^查看\s*/, '').replace(/\s*Tab$/, '');
  }
  return route.name || route.path || '';
};

const routeAllowed = (route: RouteLike, access: CommandPaletteAccess) => {
  if (route.access && access[route.access] === false) {
    return false;
  }
  const permissionCodes = normalizePermissions(route.permission)
    .map((item) => item.code?.trim())
    .filter(Boolean) as string[];
  if (!permissionCodes.length || !access.hasPermissionCodes) {
    return true;
  }
  return access.hasPermissionCodes(...permissionCodes);
};

const appDecorTabHref = (
  tab: string,
  options: BuildCommandPaletteOptions,
) => {
  const params =
    options.currentPathname === '/growth-config/app-decor-editor'
      ? new URLSearchParams(options.currentSearch || '')
      : new URLSearchParams();
  params.set('tab', tab);
  return `/growth-config/app-decor-editor?${params.toString()}`;
};

const targetForRoute = (
  route: RouteLike,
  options: BuildCommandPaletteOptions,
): Pick<CommandPaletteItem, 'href' | 'type'> | null => {
  if (!route.path && !route.redirect) return null;
  if (route.redirect) {
    return { href: route.redirect, type: 'menu' };
  }
  const path = route.path || '';
  if (path.includes(':') || path.endsWith('*')) return null;
  const appDecorTab = APP_DECOR_TAB_BY_PATH[path];
  if (appDecorTab) {
    return { href: appDecorTabHref(appDecorTab, options), type: 'tab' };
  }
  const adSlotTabHref = AD_SLOT_TAB_TARGET_BY_PATH[path];
  if (adSlotTabHref) {
    return { href: adSlotTabHref, type: 'tab' };
  }
  if (!route.component) return null;
  return { href: path, type: 'menu' };
};

export const buildCommandPaletteItems = (
  access: CommandPaletteAccess,
  options: BuildCommandPaletteOptions = {},
) => {
  const items: CommandPaletteItem[] = [];
  const seen = new Set<string>();

  const walk = (
    routeItems: RouteLike[],
    labels: string[],
    namePath: string[],
    parentAllowed: boolean,
  ) => {
    routeItems.forEach((route) => {
      const allowed = parentAllowed && routeAllowed(route, access);
      if (!allowed) return;

      const label = routeLabel(route, namePath);
      const nextLabels = label ? [...labels, label] : labels;
      const nextNamePath = route.name ? [...namePath, route.name] : namePath;
      const target = targetForRoute(route, options);

      if (target && label) {
        const key = `${target.type}:${target.href}:${label}`;
        if (!seen.has(key)) {
          seen.add(key);
          items.push({
            key,
            title: label,
            breadcrumb: nextLabels.slice(0, -1),
            href: target.href,
            type: target.type,
          });
        }
      }

      if (route.routes?.length) {
        walk(route.routes, nextLabels, nextNamePath, allowed);
      }
    });
  };

  walk(routes as RouteLike[], [], [], true);
  return items;
};

export const filterCommandPaletteItems = (
  items: CommandPaletteItem[],
  keyword: string,
) => {
  const query = keyword.trim();
  if (!query) return items.slice(0, 12);
  return items
    .filter((item) =>
      menuLabelMatches(
        [item.title, ...item.breadcrumb, item.href].join(' '),
        query,
      ),
    )
    .slice(0, 20);
};
